import { Injectable } from '@angular/core';
import { map } from 'rxjs/operators';
import EncodeUtils from '../utility/encode-utils';
import { DesktopService } from './desktop.service';
import type { IBluetoothDevice } from './bluetooth.service';

@Injectable({ providedIn: 'root' })
export class DesktopBluetoothService {
  constructor(private _desktop: DesktopService) {}

  getConnectedDevices(): Promise<IBluetoothDevice[]> {
    this._desktop.send({ Method: 'getBluetoothDevices' });
    return this._desktop
      .on('getBluetoothDevices')
      .pipe(
        map((res) => {
          const devices = (res?.Message || []) as any[];
          return devices.map((d) => this._mapDevice(d));
        })
      )
      .toPromise();
  }

  sendDataToDevice(
    deviceId: string,
    data: string | ArrayBuffer | number[] | Uint8Array,
    timeout = 30000
  ): Promise<void> {
    if (!deviceId) {
      return Promise.reject('Device id is required');
    }

    if (typeof data === 'string') {
      this._desktop.send({
        Method: 'bluetoothSend',
        DeviceId: deviceId,
        Text: data,
        CodePage: 'windows1251',
        Timeout: timeout,
      });
    } else {
      this._desktop.send({
        Method: 'bluetoothSend',
        DeviceId: deviceId,
        Data: this._toBase64(data),
        Timeout: timeout,
      });
    }

    return this._desktop
      .on('bluetoothSend')
      .pipe(
        map(() => {
          return;
        })
      )
      .toPromise();
  }

  /**
   * Desktop opens a fresh RFCOMM session per send; nothing to keep open.
   */
  disconnect(): Promise<void> {
    return Promise.resolve();
  }

  private _mapDevice(device: any): IBluetoothDevice {
    // Prefer Windows device Id for sendDataToDevice / printer.address.
    const id = device?.Id || device?.id || '';
    const address = id || device?.Address || device?.address || '';

    return {
      id,
      uuid: id,
      address,
      class: 0,
      name: device?.Name || device?.name || address,
      rssi: 0,
    };
  }

  private _toBase64(
    data: string | ArrayBuffer | number[] | Uint8Array
  ): string {
    if (typeof data === 'string') {
      return window.btoa(data);
    }

    if (data instanceof ArrayBuffer) {
      return EncodeUtils.arrayBufferToBase64(new Uint8Array(data));
    }

    if (Array.isArray(data)) {
      return EncodeUtils.arrayBufferToBase64(new Uint8Array(data));
    }

    return EncodeUtils.arrayBufferToBase64(data);
  }
}
